// The Calendar's reads (CALENDAR-C1): one cache prefix per company, so any write refreshes the
// calendars, the window's occurrences, an open event and the board's "Events" chips together.
import { queryOptions } from "@tanstack/react-query";
import type { Api } from "../../api/client";

export const calendarKeys = {
  root: (co: string) => ["company", co, "calendar"] as const,
  calendars: (co: string) => [...calendarKeys.root(co), "calendars"] as const,
  /** A window's occurrences for one selection of calendars (the shown and followed ids). */
  events: (co: string, from: string, to: string, selection = "") =>
    [...calendarKeys.root(co), "events", from, to, selection] as const,
  /** The mutation key of a change to which calendars are read (follow, unfollow, show/hide). */
  selection: (co: string) => [...calendarKeys.root(co), "selection"] as const,
  event: (co: string, id: string, at: string | null) =>
    [...calendarKeys.root(co), "event", id, at ?? ""] as const,
  tasks: (co: string, from: string, to: string) =>
    [...calendarKeys.root(co), "tasks", from, to] as const,
  /** CAL-WORK-LAYERS: the layers' chips in a window, per selection (as the events). */
  items: (co: string, from: string, to: string, selection = "") =>
    [...calendarKeys.root(co), "items", from, to, selection] as const,
  shares: (co: string, id: string) => [...calendarKeys.root(co), "shares", id] as const,
  feed: (co: string) => [...calendarKeys.root(co), "feed"] as const,
};

export const calendarsQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: calendarKeys.calendars(co),
    queryFn: () => api.client.calendars.list({ params: { co } }),
  });

/** The key part naming a selection: the followed calendars' ids, shown ones marked. */
export const selectionOf = (cals: readonly { id: string; shown: boolean }[]) =>
  cals
    .map((c) => `${c.id}${c.shown ? "" : ":hidden"}`)
    .sort()
    .join(",");

export const occurrencesQuery = (api: Api, co: string, from: string, to: string, selection = "") =>
  queryOptions({
    queryKey: calendarKeys.events(co, from, to, selection),
    queryFn: () => api.client.calendarEvents.list({ params: { co }, query: { from, to } }),
  });

export const eventQuery = (api: Api, co: string, id: string, at: string | null) =>
  queryOptions({
    queryKey: calendarKeys.event(co, id, at),
    queryFn: () =>
      api.client.calendarEvents.get({
        params: { co, id },
        query: at ? { occurrence_start: at } : {},
      }),
  });

export const calendarTasksQuery = (api: Api, co: string, from: string, to: string) =>
  queryOptions({
    queryKey: calendarKeys.tasks(co, from, to),
    queryFn: () => api.client.calendarTasks.list({ params: { co }, query: { from, to } }),
  });

export const calendarItemsQuery = (
  api: Api,
  co: string,
  from: string,
  to: string,
  selection = "",
) =>
  queryOptions({
    queryKey: calendarKeys.items(co, from, to, selection),
    queryFn: () => api.client.calendarItems.list({ params: { co }, query: { from, to } }),
  });

export const sharesQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: calendarKeys.shares(co, id),
    queryFn: () => api.client.calendarShares.list({ params: { co, id } }),
  });

export const feedQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: calendarKeys.feed(co),
    queryFn: () => api.client.calendarFeed.get({ params: { co } }),
  });
